import { BreadcrumbItem } from "@episerver/ui-framework";
import { createSelector, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { fetchEnumsByType } from "../../EnumManager/reducers/asyncThunks";
import { Organization } from "../../OrganizationManager/model/organization";
import { Contact, ContactTab, EnumData, KeyValueOption, CustomerAccount } from "../model/contact";
import {
    createCustomerAccount,
    fetchAddressesByContactId,
    fetchContactById,
    fetchCurrencies,
    fetchLanguages,
    fetchOrganizations,
    fetchUserByContactId,
    updateContact,
} from "./asyncThunks";
import { Address } from "../../AddressManager/model/address";
import { ContactNote } from "../../ContactNoteManager/model/ContactNote";
import { Property } from "../../../businessFoundation/MetaClassData/MetaClassDataList/MetaClassDataModel";

export const contactTitle = { Add: "Create ", Edit: "Edit " };

export const initialContactDetailState = {
    title: "",
    contact: {
        primaryKeyId: null,
        fullName: "",
        firstName: "",
        middleName: "",
        lastName: "",
        email: "",
        ownerId: "0",
        customerGroupId: "0",
        customerGroup: "",
        preferredCurrencyId: "0",
        preferredLanguageId: "0",
        preferredShippingAddressId: "0",
        preferredBillingAddressId: "0",
        registrationSource: "",
        userId: "",
        extendedProperties: [] as Property[],
        contactNotes: [] as ContactNote[],
        addresses: [] as Address[],
        acceptMarketingEmail: false,
    } as Contact,
    extendedPropertyErrors: [] as Error[],
    customerGroups: [] as EnumData[],
    currencies: [{ value: "[ No value ]", key: "0" }] as KeyValueOption[],
    languages: [{ value: "[ No value ]", key: "0" }] as KeyValueOption[],
    addresses: [{ name: "[ No value ]", primaryKeyId: "0" }] as Address[],
    organizations: [{ name: "[ No value ]", primaryKeyId: "0" }] as Organization[],
    breadCrumbItems: [
        {
            title: "Contacts",
            level: 1,
            link: "#/Contacts",
        },
    ] as BreadcrumbItem[],
    activeTab: ContactTab.Details,
    customerAccount: {
        open: false,
        userId: 0,
        username: "",
        password: "",
        description: "",
        email: "",
        approved: true,
    } as CustomerAccount,

    error: null,
    errorOpen: false,
};

export const contactDetailSlice = createSlice({
    name: "contactDetail",
    initialState: initialContactDetailState,
    reducers: {
        changeTitle: (state, action): void => {
            state.title = action.payload;
        },
        setParentId: (state, action): void => {
            state.contact.ownerId = action.payload;
        },
        setCustomerGroupId: (state, action): void => {
            state.contact.customerGroupId = action.payload;
        },
        updateBreadcrumb: (state, action: { payload: BreadcrumbItem }): void => {
            state.breadCrumbItems = [
                ...state.breadCrumbItems.filter((item: BreadcrumbItem) => item.level !== action.payload.level),
                action.payload,
            ];
        },
        onChangeProperty: (state, action): void => {
            state.contact = action.payload;
        },
        changeExtendedPropertyErrors: (state, action): void => {
            let { name, message } = action.payload;
            let error = state.extendedPropertyErrors.find((error: Error) => error.name === name);
            if (error) {
                state.extendedPropertyErrors = state.extendedPropertyErrors
                    .filter((error: Error) => {
                        if (error.name === name && message === "") return false;
                        return true;
                    })
                    .map((error: Error) => {
                        if (error.name === name) return { ...error, message: message };
                        return error;
                    });
            } else {
                if (message) {
                    state.extendedPropertyErrors.push({ name: name, message: message });
                }
            }
        },
        changeActiveTab: (state, action): void => {
            state.activeTab = action.payload;
        },
        reset: (state): void => {
            state.contact = initialContactDetailState.contact;
        },
        onAddAcountDialog: (state, action): void => {
            state.customerAccount = action.payload;
        },

        onChangeAccount: (state, action): void => {
            let { name, value } = action.payload;
            state.customerAccount = {
                ...state.customerAccount,
                [name]: value,
            };
        },
        setError: (state, action): void => {
            state.error = action.payload;
        },
        onAddOrUpdateContactNote: (state, action): void => {
            const {idx, isNew, ...contactNote} = action.payload
            if (isNew) {
                state.contact.contactNotes.push(contactNote);
            } else {
                state.contact.contactNotes = state.contact.contactNotes.map((t, index) => {
                    if (index == idx) return contactNote;
                    return t;
                });
            }
        },
        onDeleteContactNote: (state, action): void => {
            let contactNotes = [...state.contact.contactNotes];
            state.contact.contactNotes = contactNotes.filter(
                (v) => action.payload.contactNoteIds.indexOf(v.contactNoteId) == -1
            );
        },

        onAddOrUpdateContactAddress: (state, action): void => {
            const {idx, isNew, ...address} = action.payload
            if (isNew) {
                state.contact.addresses.push(address);
            } else {
                state.contact.addresses = state.contact.addresses.map((t, index) => {
                    if (index == idx) return address;
                    return t;
                });
            }
        },
        onDeleteContactAddress: (state, action): void => {
            let addresses = [...state.contact.addresses];
            state.contact.addresses = addresses.filter((v) => action.payload.primaryKeyId != v.primaryKeyId);
        },
    },
    extraReducers: {
        [`${fetchContactById.fulfilled}`]: (state, action): void => {
            state.error = null;
            state.contact = action.payload;
        },
        [`${fetchCurrencies.fulfilled}`]: (state, action): void => {
            state.currencies = [...initialContactDetailState.currencies, ...action.payload];
        },
        [`${fetchLanguages.fulfilled}`]: (state, action): void => {
            state.languages = [...initialContactDetailState.languages, ...action.payload];
        },
        [`${fetchEnumsByType.fulfilled}`]: (state, action): void => {
            state.customerGroups = [...initialContactDetailState.customerGroups, ...action.payload.enums];
        },
        [`${fetchAddressesByContactId.fulfilled}`]: (state, action): void => {
            state.addresses = [...initialContactDetailState.addresses, ...action.payload];
        },
        [`${fetchOrganizations.fulfilled}`]: (state, action): void => {
            state.organizations = [...initialContactDetailState.organizations, ...action.payload];
        },
        [`${fetchUserByContactId.fulfilled}`]: (state, action): void => {
            state.customerAccount = action.payload;
        },
        [`${updateContact.rejected}`]: (state, action): void => {
            state.error = action.payload;
        },
        [`${createCustomerAccount.fulfilled}`]: (state, action): void => {
            state.error = action.payload.errors.join(" ");
            state.customerAccount = action.payload.account;
            state.contact.userId = action.payload.account.username;    
        },
    },
});

// Actions
export const {
    changeTitle,
    setParentId,
    setCustomerGroupId,
    onChangeProperty,
    updateBreadcrumb,
    changeExtendedPropertyErrors,
    changeActiveTab,
    reset,
    onAddAcountDialog,
    onChangeAccount,
    setError,
    onAddOrUpdateContactNote,
    onDeleteContactNote,
    onAddOrUpdateContactAddress,
    onDeleteContactAddress,
} = contactDetailSlice.actions;

// Selectors
export const titleSelector = createSelector(
    (state: RootState): string => state.contactDetail.title,
    (title: string): string => title
);

export const contactSelector = createSelector(
    (state: RootState): Contact => state.contactDetail.contact,
    (contact: Contact): Contact => contact
);

export const currenciesSelector = createSelector(
    (state: RootState): KeyValueOption[] => state.contactDetail.currencies,
    (currencies: KeyValueOption[]): KeyValueOption[] => currencies
);

export const languagesSelector = createSelector(
    (state: RootState): KeyValueOption[] => state.contactDetail.languages,
    (languages: KeyValueOption[]): KeyValueOption[] => languages
);

export const customerGroupsSelector = createSelector(
    (state: RootState): EnumData[] => state.contactDetail.customerGroups,
    (customerGroups: EnumData[]): EnumData[] => customerGroups
);

export const addressesSelector = createSelector(
    (state: RootState): Address[] => state.contactDetail.addresses,
    (addresses: Address[]): Address[] => addresses
);

export const organizationsSelector = createSelector(
    (state: RootState): Organization[] => state.contactDetail.organizations,
    (organizations: Organization[]): Organization[] => organizations
);

export const breadcrumbItemsSelector = createSelector(
    (state: RootState): BreadcrumbItem[] => state.contactDetail.breadCrumbItems,
    (breadCrumbItems: BreadcrumbItem[]): BreadcrumbItem[] => breadCrumbItems
);

export const extendedPropertyErrorsSelector = createSelector(
    (state: RootState): Error[] => state.contactDetail.extendedPropertyErrors,
    (extendedPropertyErrors: Error[]): Error[] => extendedPropertyErrors
);

export const activeTabSelector = createSelector(
    (state: RootState): ContactTab => state.contactDetail.activeTab,
    (activeTab: ContactTab): ContactTab => activeTab
);

export const customerAccountSelector = createSelector(
    (state: RootState): CustomerAccount => state.contactDetail.customerAccount,
    (customerAccount: CustomerAccount): CustomerAccount => customerAccount
);

export const errorSelector = createSelector(
    (state: RootState): any => state.contactDetail.error,
    (error: any) => error
);

